import {
  Box,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  Button,
  Spinner,
  Checkbox,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  useToast,
  Tooltip,
  IconButton,
} from '@chakra-ui/react';
import React from 'react';
import { FiPlus } from 'react-icons/fi';
import { BiEdit } from 'react-icons/bi';
import dayjs from 'dayjs';
import useSWR from 'swr';
import xhr from 'xhr';

const PAYMENT_METHODS = [
  'Transferencia bancaria',
  'Efectivo',
  'Nequi',
  'Daviplata',
  'Consignacion',
  'PSE',
  'Otro',
];

async function fetchSchoolPayments(data) {
  const { studentId, schoolId } = data;
  const res = await xhr(
    `/payment-schools?filters[student][id][$eq]=${studentId}&filters[school][id][$eq]=${schoolId}&populate=program_type,grade,technical_career,course,image&sort[0]=payment_date:desc&pagination[pageSize]=200`
  );
  return res.data.data || [];
}

async function fetchSchoolPrograms(data) {
  const { schoolId } = data;
  const res = await xhr(
    `/school-programs?filters[school][id][$eq]=${schoolId}&filters[active][$eq]=true&populate=program_type,grade,technical_career,course&pagination[pageSize]=200`
  );
  return res.data.data || [];
}

// -------------------------------------------------------------------
// SchoolModalSubject — loads subjects from the payment's program
// -------------------------------------------------------------------
function SchoolModalSubject({ studentId, payment, onClose, onSuccess }) {
  const toast = useToast();
  const [fetchingSubjects, setFetchingSubjects] = React.useState(true);
  const [subjects, setSubjects] = React.useState([]);
  const [subjectEnrollments, setSubjectEnrollments] = React.useState([]);
  const [subjectsEnrollmentsRaw, setSubjectsEnrollmentsRaw] = React.useState([]);
  const [conceptList, setConceptList] = React.useState([]);
  const [isPosting, setIsPosting] = React.useState(false);
  const [success, setSuccess] = React.useState(false);

  const [isMenu, setIsMenu] = React.useState(false);
  const [currentPeriod, setCurrentPeriod] = React.useState('primer');
  const [subjectsPeriod, setSubjectsPeriod] = React.useState({});
  const [isFetchingEnroll, setIsFetchingEnroll] = React.useState(false);

  // Resolve the program from the payment
  const paymentAttrs = payment.attributes || payment;
  const gradeData = paymentAttrs.grade?.data || paymentAttrs.grade;
  const tcData = paymentAttrs.technical_career?.data || paymentAttrs.technical_career;
  const courseData = paymentAttrs.course?.data || paymentAttrs.course;
  const ptData = paymentAttrs.program_type?.data || paymentAttrs.program_type;

  const programEntity = gradeData || tcData || courseData;
  const programField = gradeData ? 'grade' : tcData ? 'technical_career' : courseData ? 'course' : null;
  const programId = programEntity?.id;
  const programName = (programEntity?.attributes || programEntity)?.name || '';
  const programTypeSlug = (ptData?.attributes || ptData)?.slug || '';
  const programTypeType = (ptData?.attributes || ptData)?.type || programField;

  // Fetch subjects for the program
  React.useEffect(() => {
    const loadSubjects = async () => {
      if (!programId || !programTypeType) {
        setFetchingSubjects(false);
        return;
      }
      try {
        setFetchingSubjects(true);
        const response = await xhr(
          `/subjects?filters[${programTypeType}][id][$eq]=${programId}&filters[visible][$eq]=true&populate=*&pagination[limit]=-1`
        );

        const data = response.data.data;

        const byPeriod = data.reduce((acc, current) => {
          const key = current.period || 'sin_periodo';
          if (acc[key]) {
            return { ...acc, [key]: [...acc[key], current] };
          }
          return { ...acc, [key]: [current] };
        }, {});

        setSubjects(data);
        setSubjectsPeriod(byPeriod);

        // Determine if period selector should show
        const isAnnualProgram =
          programTypeSlug === 'bachillerato-anual-para-adolescentes' ||
          programTypeSlug === 'primaria-anual-para-ninos' ||
          programTypeSlug === 'bachillerato-flexible';
        const showPeriod = programTypeType === 'technical_career' || isAnnualProgram;
        setIsMenu(showPeriod);
      } catch (error) {
        console.error(error);
      } finally {
        setFetchingSubjects(false);
      }
    };
    loadSubjects();
  }, [programId, programTypeType, programTypeSlug]);

  // Fetch existing subject enrollments
  React.useEffect(() => {
    const getSubjectEnrollments = async () => {
      if (!programId || !programTypeType) return;
      setIsFetchingEnroll(true);
      try {
        const response = await xhr(
          `/subject-enrollments?filters[student][id][$eq]=${studentId}&filters[subject][id][$gte]=1&filters[subject][${programTypeType.replace(
            '-',
            '_'
          )}][id][$eq]=${programId}&pagination[limit]=-1&populate=*`
        );
        const filterIsTrial = response.data.data.filter((e) => e.is_trial !== true);
        setSubjectsEnrollmentsRaw(response.data.data);
        setSubjectEnrollments(filterIsTrial.map((el) => el.subject.id));
      } catch (error) {
        console.error(error);
      } finally {
        setIsFetchingEnroll(false);
      }
    };
    getSubjectEnrollments();
  }, [programId, programTypeType, studentId]);

  const handleCheck = (item, checked) => {
    if (checked) {
      setConceptList((prev) => [...prev, item]);
    } else {
      setConceptList((prev) => prev.filter((el) => el.id !== item.id));
    }
  };

  const conceptListIds = conceptList.map((el) => el.id);

  // Subjects to display (period-filtered if needed)
  const displaySubjects = React.useMemo(() => {
    if (isMenu && subjectsPeriod[currentPeriod]) {
      return subjectsPeriod[currentPeriod];
    }
    return subjects;
  }, [isMenu, currentPeriod, subjectsPeriod, subjects]);

  // Post subject enrollments
  const postSubjects = async () => {
    try {
      setIsPosting(true);

      const idSubjectsEnrollments = subjectsEnrollmentsRaw.map((el) =>
        el.subject ? el.subject.id : el.course ? el.course.id : null
      );

      const filterSubject = conceptListIds.filter((e) => idSubjectsEnrollments.indexOf(e) > -1);

      // If a subject was enrolled as trial, update it
      if (filterSubject.length === 1) {
        const dataPayAlready = subjectsEnrollmentsRaw.find(
          (el) => (el.subject ? el.subject.id : el.course?.id) === filterSubject[0]
        );
        if (dataPayAlready) {
          await xhr.put(`subject-enrollments/${dataPayAlready.id}`, {
            data: { is_trial: false },
          });
        }
      }

      const removeIdAlreadyEnroll = conceptListIds.filter((el) => el !== filterSubject[0]);

      if (removeIdAlreadyEnroll.length > 0) {
        for (const subjectId of removeIdAlreadyEnroll) {
          await xhr.post('subject-enrollments', {
            data: {
              student: studentId,
              subject: subjectId,
              [programTypeType.replace('-', '_')]: programId,
            },
          });
        }
      }

      setConceptList([]);
      setSuccess(true);

      // Refresh enrollments
      const response = await xhr(
        `/subject-enrollments?filters[student][id][$eq]=${studentId}&filters[subject][id][$gte]=1&filters[subject][${programTypeType.replace(
          '-',
          '_'
        )}][id][$eq]=${programId}&pagination[limit]=-1&populate=*`
      );
      const filterIsTrial = response.data.data.filter((e) => e.is_trial !== true);
      setSubjectsEnrollmentsRaw(response.data.data);
      setSubjectEnrollments(filterIsTrial.map((el) => el.subject.id));

      toast({
        title: 'Las materias se han matriculado correctamente!',
        status: 'success',
        position: 'bottom',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error(error);
      toast({
        title: 'Error al matricular materias',
        status: 'error',
        position: 'bottom',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsPosting(false);
    }
  };

  const noProgramLinked = !programId || !programTypeType;

  return (
    <Box>
      {noProgramLinked ? (
        <Text color="gray.500" fontSize="sm">
          Este pago no tiene un programa asociado. Para matricular materias el pago debe tener un tipo de programa y programa seleccionado.
        </Text>
      ) : (
        <>
          <Text fontWeight="500" mb={1}>
            {programName}
          </Text>

          {isMenu && Object.keys(subjectsPeriod).length > 0 && (
            <Select
              onChange={(e) => setCurrentPeriod(e.target.value)}
              width="40%"
              marginBottom="15px"
              size="sm"
            >
              {Object.keys(subjectsPeriod)
                .sort()
                .map((el) => (
                  <option key={el} value={el}>
                    {el.charAt(0).toUpperCase() + el.slice(1)}
                  </option>
                ))}
            </Select>
          )}

          <Box border="1px solid" borderColor="gray.200" borderRadius="md" mb={4}>
            <Box
              backgroundColor="#cacbf9"
              color="#3d37a5"
              fontWeight="500"
              padding="8px"
              paddingX="10px"
              borderTopRadius="md"
              fontSize="sm"
            >
              Que materias paga el estudiante?
            </Box>

            {fetchingSubjects || isFetchingEnroll ? (
              <Flex justifyContent="center" paddingY="15px">
                <Spinner />
              </Flex>
            ) : (
              <Box
                display="grid"
                gridTemplateColumns="1fr 1fr"
                padding="10px"
                gridGap="10px"
                maxHeight="350px"
                overflowY="auto"
              >
                {displaySubjects.length > 0 ? (
                  displaySubjects.map((el, i) => {
                    const isEnrolled = subjectEnrollments.includes(el.id);
                    return (
                      <Flex key={i}>
                        <Checkbox
                          isChecked={isEnrolled || conceptListIds.includes(el.id)}
                          isDisabled={isEnrolled}
                          textDecoration={isEnrolled ? 'line-through' : 'none'}
                          cursor={isEnrolled ? 'not-allowed' : 'pointer'}
                          onChange={(event) => handleCheck(el, event.target.checked)}
                          size="sm"
                        >
                          <Text fontSize="sm">{el.name}</Text>
                        </Checkbox>
                      </Flex>
                    );
                  })
                ) : (
                  <Box textAlign="center" color="gray.500" fontSize="sm" gridColumn="1 / -1" py={3}>
                    No se encontraron materias
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {success && (
            <Box bg="green.50" color="green.700" p={3} borderRadius="md" mb={3} fontSize="sm">
              Las materias se han matriculado correctamente
            </Box>
          )}

          <Flex gap={3}>
            <Button
              colorScheme="purple"
              bg="#574feb"
              size="sm"
              isDisabled={isPosting || conceptListIds.length === 0}
              isLoading={isPosting}
              onClick={postSubjects}
            >
              Matricular Materias
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              Cerrar
            </Button>
          </Flex>
        </>
      )}
    </Box>
  );
}

// -------------------------------------------------------------------
// SchoolPagosTab — main component
// -------------------------------------------------------------------
export default function SchoolPagosTab({ studentId, schoolId }) {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [creating, setCreating] = React.useState(false);
  const [file, setFile] = React.useState(null);
  const fileInputRef = React.useRef(null);
  const [isOpenSubject, setIsOpenSubject] = React.useState(false);
  const [selectedPayment, setSelectedPayment] = React.useState(null);

  const [newPayment, setNewPayment] = React.useState({
    program_type: '',
    program_id: '',
    program_field: '',
    amount: '',
    payment_date: dayjs().format('YYYY-MM-DD'),
    payment_method: '',
    description: '',
  });

  const { data: payments, mutate } = useSWR(
    { type: 'school-student-payments', studentId, schoolId },
    fetchSchoolPayments,
    { revalidateOnFocus: false }
  );

  const { data: schoolPrograms } = useSWR(
    { type: 'school-programs-for-payments', schoolId },
    fetchSchoolPrograms,
    { revalidateOnFocus: false }
  );

  // Group programs by program_type
  const programTypeGroups = React.useMemo(() => {
    if (!schoolPrograms) return [];
    const map = {};
    schoolPrograms.forEach((sp) => {
      const attrs = sp.attributes || sp;
      const pt = attrs.program_type?.data?.attributes || attrs.program_type;
      const ptId = attrs.program_type?.data?.id || attrs.program_type?.id;
      if (!pt || !ptId) return;

      if (!map[ptId]) {
        map[ptId] = { id: ptId, name: pt.name, type: pt.type, programs: [] };
      }

      const grade = attrs.grade?.data || attrs.grade;
      const tc = attrs.technical_career?.data || attrs.technical_career;
      const course = attrs.course?.data || attrs.course;
      const program = grade || tc || course;
      const field = grade ? 'grade' : tc ? 'technical_career' : course ? 'course' : null;

      if (program && field) {
        const pAttrs = program.attributes || program;
        const existing = map[ptId].programs.find((p) => p.id === (program.id || program.data?.id));
        if (!existing) {
          map[ptId].programs.push({
            id: program.id || program.data?.id,
            name: pAttrs.name,
            field,
          });
        }
      }
    });
    return Object.values(map);
  }, [schoolPrograms]);

  // Programs filtered by selected program_type
  const filteredPrograms = React.useMemo(() => {
    if (!newPayment.program_type) return [];
    const group = programTypeGroups.find((g) => g.id === Number(newPayment.program_type));
    return group?.programs || [];
  }, [newPayment.program_type, programTypeGroups]);

  const handleProgramTypeChange = (ptId) => {
    setNewPayment((p) => ({
      ...p,
      program_type: ptId,
      program_id: '',
      program_field: '',
    }));
  };

  const handleProgramChange = (val) => {
    const program = filteredPrograms.find((p) => p.id === Number(val));
    setNewPayment((p) => ({
      ...p,
      program_id: val,
      program_field: program?.field || '',
    }));
  };

  const resetForm = () => {
    setNewPayment({
      program_type: '',
      program_id: '',
      program_field: '',
      amount: '',
      payment_date: dayjs().format('YYYY-MM-DD'),
      payment_method: '',
      description: '',
    });
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCreate = async () => {
    if (!newPayment.payment_method || !newPayment.amount) {
      toast({
        title: 'Campos requeridos',
        description: 'Monto y metodo de pago son obligatorios',
        status: 'warning',
        position: 'bottom',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    try {
      setCreating(true);

      const payload = {
        student: Number(studentId),
        school: schoolId,
        amount: Number(newPayment.amount),
        payment_date: newPayment.payment_date,
        payment_method: newPayment.payment_method,
        description: newPayment.description,
        status: 'paid',
      };

      if (newPayment.program_type) {
        payload.program_type = Number(newPayment.program_type);
      }
      if (newPayment.program_id && newPayment.program_field) {
        payload[newPayment.program_field] = Number(newPayment.program_id);
      }

      const res = await xhr.post('/payment-schools', { data: payload });
      const newId = res.data.data?.id || res.data.id;

      // Upload image if provided
      if (file && newId) {
        const formData = new FormData();
        formData.append('files', file);
        formData.append('ref', 'api::payment-school.payment-school');
        formData.append('refId', newId);
        formData.append('field', 'image');

        await xhr('/upload', {
          method: 'post',
          data: formData,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      toast({
        title: 'Pago registrado exitosamente',
        status: 'success',
        position: 'bottom',
        duration: 3000,
        isClosable: true,
      });

      resetForm();
      onClose();
      mutate();
    } catch (error) {
      const msg = error.response?.data?.error?.message || error.message || 'Error al registrar pago';
      toast({
        title: 'Error al registrar pago',
        description: msg,
        status: 'error',
        position: 'bottom',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCreating(false);
    }
  };

  const getConceptLabel = (payment) => {
    const attrs = payment.attributes || payment;
    const grade = attrs.grade?.data?.attributes || attrs.grade;
    const tc = attrs.technical_career?.data?.attributes || attrs.technical_career;
    const course = attrs.course?.data?.attributes || attrs.course;
    return grade?.name || tc?.name || course?.name || 'Pago registrado';
  };

  if (!payments) {
    return (
      <Box p={4}>
        <Spinner />
      </Box>
    );
  }

  return (
    <Box>
      <Flex mb={4}>
        <Button
          leftIcon={<FiPlus />}
          size="sm"
          colorScheme="purple"
          variant="outline"
          onClick={onOpen}
        >
          Registrar pago
        </Button>
      </Flex>

      {payments.length === 0 ? (
        <Text color="gray.500" fontSize="sm">
          Este estudiante aun no tiene pagos registrados
        </Text>
      ) : (
        <TableContainer>
          <Table variant="striped" size="sm">
            <Thead>
              <Tr>
                <Th>Fecha</Th>
                <Th>Valor</Th>
                <Th>Medio de pago</Th>
                <Th>Descripcion</Th>
                <Th>Soporte</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {payments.map((payment) => {
                const attrs = payment.attributes || payment;
                const imageData = attrs.image?.data?.attributes || attrs.image;

                return (
                  <Tr key={payment.id}>
                    <Td fontSize="sm">
                      {attrs.payment_date
                        ? dayjs(attrs.payment_date).format('DD-MM-YYYY')
                        : '-'}
                    </Td>
                    <Td fontWeight="600" fontSize="sm">
                      ${Number(attrs.amount || 0).toLocaleString('es')}
                    </Td>
                    <Td fontSize="sm">{attrs.payment_method || '-'}</Td>
                    <Td>
                      <Box whiteSpace="break-spaces" maxW="150px">
                        <Text fontSize="sm" color="gray.600">
                          {attrs.description || '-'}
                        </Text>
                      </Box>
                    </Td>
                    <Td>
                      {imageData?.url ? (
                        <Text
                          fontSize="sm"
                          fontWeight="500"
                          cursor="pointer"
                          onClick={() => window.open(imageData.url)}
                        >
                          Ver
                        </Text>
                      ) : (
                        <Text fontSize="sm" color="gray.400">
                          -
                        </Text>
                      )}
                    </Td>
                    <Td px={1}>
                      <Tooltip label="Matricular materias" placement="left" hasArrow>
                        <IconButton
                          icon={<BiEdit size={22} />}
                          size="sm"
                          bg="purple.50"
                          color="purple.500"
                          _hover={{ bg: 'purple.100' }}
                          aria-label="Matricular materias"
                          onClick={() => {
                            setSelectedPayment(payment);
                            setIsOpenSubject(true);
                          }}
                        />
                      </Tooltip>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {/* Modal: Registrar Pago */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader>Registrar pago</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" gap={4}>
              <FormControl>
                <FormLabel>Tipo de programa</FormLabel>
                <Select
                  placeholder="Seleccionar tipo"
                  value={newPayment.program_type}
                  onChange={(e) => handleProgramTypeChange(e.target.value)}
                >
                  {programTypeGroups.map((pt) => (
                    <option key={pt.id} value={pt.id}>
                      {pt.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Programa</FormLabel>
                <Select
                  placeholder="Seleccionar programa"
                  value={newPayment.program_id}
                  onChange={(e) => handleProgramChange(e.target.value)}
                  isDisabled={!newPayment.program_type}
                >
                  {filteredPrograms.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <Flex gap={4}>
                <FormControl isRequired flex="1">
                  <FormLabel>Monto</FormLabel>
                  <Input
                    type="number"
                    value={newPayment.amount}
                    onChange={(e) =>
                      setNewPayment((p) => ({ ...p, amount: e.target.value }))
                    }
                    placeholder="0"
                  />
                </FormControl>
                <FormControl isRequired flex="1">
                  <FormLabel>Fecha de pago</FormLabel>
                  <Input
                    type="date"
                    value={newPayment.payment_date}
                    onChange={(e) =>
                      setNewPayment((p) => ({ ...p, payment_date: e.target.value }))
                    }
                  />
                </FormControl>
              </Flex>

              <FormControl isRequired>
                <FormLabel>Medio de pago</FormLabel>
                <Select
                  placeholder="Seleccionar medio"
                  value={newPayment.payment_method}
                  onChange={(e) =>
                    setNewPayment((p) => ({ ...p, payment_method: e.target.value }))
                  }
                >
                  {PAYMENT_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Descripcion</FormLabel>
                <Textarea
                  value={newPayment.description}
                  onChange={(e) =>
                    setNewPayment((p) => ({ ...p, description: e.target.value }))
                  }
                  placeholder="Concepto del pago, mes, observaciones..."
                  rows={2}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Soporte de pago</FormLabel>
                <Input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={(e) => setFile(e.target.files[0] || null)}
                  pt={1}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Imagen del comprobante de pago (opcional)
                </Text>
              </FormControl>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleCreate}
              isLoading={creating}
            >
              Registrar pago
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal: Matricular Materias */}
      <Modal
        isOpen={isOpenSubject}
        onClose={() => {
          setIsOpenSubject(false);
          setSelectedPayment(null);
        }}
        size="2xl"
      >
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader fontSize="lg">Matricular Materias</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {isOpenSubject && selectedPayment && (
              <SchoolModalSubject
                studentId={studentId}
                payment={selectedPayment}
                onClose={() => {
                  setIsOpenSubject(false);
                  setSelectedPayment(null);
                }}
                onSuccess={() => {
                  // Keep modal open so they can see success + already-enrolled state
                }}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
